import { css, cx } from "@emotion/css";
import { ExclamationTriangleIcon, InfoCircleFilledIcon, ThumbsUpIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, fontSize, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { DesignSystemLinkHref } from "../../routing";
import { useOctopusLinkComponent } from "../../routing";
import { ExternalLink } from "../Link";
export interface NotificationProps {
    type: NotificationType;
    title: string;
    description: string;
    links?: Array<NotificationLinkType>;
    onNotificationCallToActionClicked: () => void;
}
export function Notification({ type, title, description, links, onNotificationCallToActionClicked }: NotificationProps) {
    return (<div className={notificationStyles.container}>
            <div className={notificationStyles.content}>
                <div className={cx(notificationStyles.title, {
            [notificationStyles.infoTitle]: type === "happy" || type === "info",
            [notificationStyles.alertTitle]: type === "warn",
            [notificationStyles.errorTitle]: type === "error",
        })}>
                    {title}
                </div>
                <div className={notificationStyles.description}>{description}</div>
                {links && (<div className={notificationStyles.links}>
                        {links.map((link, i) => (<span key={i}>
                                {i > 0 && " | "}
                                <NotificationLink link={link} onNotificationCallToActionClicked={onNotificationCallToActionClicked}/>
                            </span>))}
                    </div>)}
            </div>
            <div className={notificationStyles.icon}>
                <NotificationIcon notificationType={type}/>
            </div>
        </div>);
}
interface NotificationLinkProps {
    link: NotificationLinkType;
    onNotificationCallToActionClicked: () => void;
}
function NotificationLink({ link, onNotificationCallToActionClicked }: NotificationLinkProps) {
    const Link = useOctopusLinkComponent();
    switch (link.type) {
        case "External":
            return (<ExternalLink href={link.href} onClick={onNotificationCallToActionClicked}>
                    {link.label}
                </ExternalLink>);
        case "Internal":
            return (<Link className={notificationStyles.linkItem} href={link.href} onClick={onNotificationCallToActionClicked}>
                    {link.label}
                </Link>);
        case "Refresh":
            return (<Link className={notificationStyles.linkItem} onClick={() => {
                    onNotificationCallToActionClicked();
                    window.location.reload();
                }} href="#">
                    {link.label}
                </Link>);
        default:
            return <span>{link.label}</span>;
    }
}
const notificationStyles = {
    container: css({
        display: "flex",
        padding: `${space[8]} ${space[12]}`,
        gap: space["8"],
    }),
    content: css({
        flexGrow: 1,
    }),
    title: css({
        font: text.body.bold.medium,
    }),
    infoTitle: css({
        color: themeTokens.color.text.info,
    }),
    alertTitle: css({
        color: themeTokens.color.text.warning,
    }),
    errorTitle: css({
        color: themeTokens.color.text.danger,
    }),
    description: css({
        fontSize: fontSize.small,
        marginTop: space[1],
        marginBottom: space[8],
    }),
    links: css({
        fontSize: fontSize.small,
    }),
    linkItem: css({
        font: text.body.regular.medium,
        textDecoration: "none",
        color: themeTokens.color.text.link.default,
    }),
    icon: css({
        display: "flex",
        alignItems: "flex-start",
    }),
};
function NotificationIcon({ notificationType }: {
    notificationType: NotificationType;
}) {
    switch (notificationType) {
        case "happy":
            return (<div className={cx(iconStyles.iconWrapper, iconStyles.infoBackground)}>
                    <ThumbsUpIcon size={"font-size"}/>
                </div>);
        case "info":
            return (<div className={cx(iconStyles.iconWrapper, iconStyles.infoBackground)}>
                    <InfoCircleFilledIcon size={"font-size"}/>
                </div>);
        case "warn":
            return (<div className={cx(iconStyles.iconWrapper, iconStyles.warningBackground)}>
                    <ExclamationTriangleIcon size={"font-size"}/>
                </div>);
        case "error":
            return (<div className={cx(iconStyles.iconWrapper, iconStyles.errorBackground)}>
                    <ExclamationTriangleIcon size={"font-size"}/>
                </div>);
    }
}
const iconStyles = {
    iconWrapper: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: borderRadius.extraSmall,
        width: space[32],
        height: space[32],
        fontSize: fontSize.large,
        color: themeTokens.color.badge.icon.primary,
    }),
    infoBackground: css({
        backgroundColor: themeTokens.color.badge.background.info,
    }),
    warningBackground: css({
        backgroundColor: themeTokens.color.badge.background.warning,
    }),
    errorBackground: css({
        backgroundColor: themeTokens.color.badge.background.danger,
    }),
};
export type NotificationType = "happy" | "info" | "warn" | "error";
export type NotificationLinkType = NotificationItemLink | NotificationItemInternalLink | NotificationItemExternalLink;
type NotificationItemInternalLink = {
    type: "Internal";
    label: string;
    href: DesignSystemLinkHref;
};
type NotificationItemExternalLink = {
    type: "External";
    label: string;
    href: string;
};
type NotificationItemLink = {
    type: "None" | "Refresh";
    label: string;
};
